import { Alert, Box, Paper, useMediaQuery, useTheme } from '@mui/material';
import { Button, Icon, IconButton, Typography } from '@malash-office/ui-components';
import { Ai } from '@/assets/icons/Ai';
import { MOBILE_BOTTOM_NAV_INSET } from '@/components/layout/mobileLayoutConstants';
import { useAgentChat } from './hooks/useAgentChat';
import ChatMessageList from './components/ChatMessageList';
import ChatSuggestions from './components/ChatSuggestions';
import ChatComposer from './components/ChatComposer';

interface AgentChatPanelProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The chat as a floating card anchored above its launcher, rather than a full-height drawer.
 *
 * The distinction matters for how the thing is used: this is an assistant you consult *while*
 * looking at a screen — "what's the stock on this?" — so it must not cover the table the question
 * is about. A drawer takes the full edge and forces the user to close it to see the answer's
 * subject.
 *
 * Visibility is `display: none` rather than unmounting, so the conversation survives close and
 * reopen. History lives in `useAgentChat` and would otherwise be lost on every dismissal.
 */
export const AgentChatPanel = ({ open, onClose }: AgentChatPanelProps) => {
  const {
    messages,
    isStreaming,
    activeTool,
    error,
    send,
    stop,
    reset,
    confirmAction,
    rejectAction,
    rateTurn
  } = useAgentChat();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        // Above the sidebar's flyout menu, so an open chat is not covered beside the sidebar.
        zIndex: theme.zIndex.modal + 50,
        display: open ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: '20px',
        ...(isSmallScreen
          ? {
              left: 12,
              right: 12,
              bottom: MOBILE_BOTTOM_NAV_INSET + 12,
              height: 'min(70vh, 560px)'
            }
          : {
              // The RTL stylis plugin mirrors `left`/`right`, so this renders on the visual
              // right — above the launcher and clear of the sidebar.
              left: 110,
              bottom: 96,
              width: 380,
              height: 540
            })
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0
        }}
      >
        <Ai />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" color="text.primary">
            עוזר AI
          </Typography>
        </Box>
        {messages.length > 0 && (
          <Button variant="outlined" size="small" onClick={reset}>
            שיחה חדשה
          </Button>
        )}
        <IconButton variant="text" size="small" onClick={onClose} aria-label="סגירת הצ׳אט">
          <Icon icon="close" size="small" color="inherit" />
        </IconButton>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        {messages.length === 0 ? (
          <ChatSuggestions onSelect={send} />
        ) : (
          <ChatMessageList
            messages={messages}
            activeTool={activeTool}
            isStreaming={isStreaming}
            onConfirm={confirmAction}
            onReject={rejectAction}
            onRate={rateTurn}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {error}
        </Alert>
      )}

      <ChatComposer isStreaming={isStreaming} onSend={send} onStop={stop} />
    </Paper>
  );
};

export default AgentChatPanel;
